import { type ReactNode, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import type { Tier } from '../hooks/use-pricing-table';
import { PricingTierRows } from './pricing-tier-rows';

type Props = {
  tiers: Tier[];
  ids: string[];
  errors: Set<number>;
  priceErrors: Set<number>;
  onChange: (t: Tier[]) => void;
};

function useReorder(tiers: Tier[], ids: string[], onChange: Props['onChange']) {
  const last = tiers.length - 1;
  return useCallback(
    (e: DragEndEvent) => {
      if (!e.over || e.active.id === e.over.id) return;
      const from = ids.indexOf(e.active.id as string);
      const to = Math.max(
        1,
        Math.min(last - 1, ids.indexOf(e.over.id as string))
      );
      if (from < 1 || from > last - 1) return; // safety: don't move first/last
      onChange(arrayMove(tiers, from, to));
    },
    [tiers, onChange, ids, last]
  );
}

function updateTier(tiers: Tier[], i: number, t: Tier): Tier[] {
  const n = [...tiers];
  n[i] = t;
  const next = n[i + 1];
  if (t.max !== null && next) n[i + 1] = { ...next, min: t.max + 1 };
  return n;
}

function useDndSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
}

function Rows({ tiers, ids, errors, priceErrors, onChange }: Props): ReactNode {
  return (
    <PricingTierRows
      tiers={tiers}
      ids={ids}
      errors={errors}
      priceErrors={priceErrors}
      onUpdate={(i, t) => onChange(updateTier(tiers, i, t))}
      onRemove={(i) => onChange(tiers.filter((_, idx) => idx !== i))}
    />
  );
}

export function PricingTierList({
  tiers,
  ids,
  errors,
  priceErrors,
  onChange,
}: Props): ReactNode {
  return (
    <DndContext
      sensors={useDndSensors()}
      collisionDetection={closestCenter}
      onDragEnd={useReorder(tiers, ids, onChange)}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <Rows
          tiers={tiers}
          ids={ids}
          errors={errors}
          priceErrors={priceErrors}
          onChange={onChange}
        />
      </SortableContext>
    </DndContext>
  );
}
